import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { differenceInDays, format, subDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface ParticipantMetric {
  participantId: string;
  avgAdherence: number;
  totalAlerts: number;
  nonAdherentDaysCount: number;
}

interface ParticipantsMetricsTableProps {
  studyId: string;
}

const ParticipantsMetricsTable: React.FC<ParticipantsMetricsTableProps> = ({
  studyId,
}) => {
  const [data, setData] = useState<ParticipantMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [showUsageHint, setShowUsageHint] = useState(false);
  const [startDate, setStartDate] = useState<string>(() =>
    format(subDays(new Date(), 15), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd"),
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>("default");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogParticipantId, setDialogParticipantId] = useState<string | null>(
    null,
  );
  const [dialogDates, setDialogDates] = useState<string[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchParticipantsMetrics = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    if (!studyId) {
      setError("Missing studyId");
      setLoading(false);
      return;
    }

    try {
      setHasRun(true);
      setLoading(true);
      setError(null);

      const response = await apiService.get(
        `${API_ENDPOINTS.STUDY_ADHERENCE_MONITOR.PARTICIPANTS_METRICS}?studyId=${studyId}&startDate=${start}&endDate=${end}`,
      );

      if (response.success) {
        setData(response.data || []);
      } else {
        setData([]);
        setError(response.error || "Failed to load participant metrics");
      }
    } catch (err: any) {
      setError(err.message || "Error fetching participant metrics");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchParticipantsMetrics(startDate, endDate);
  };

  const handleResetDateRange = (days: number) => {
    const newStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const newEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleShowNonAdherentDays = async (participantId: string) => {
    setDialogOpen(true);
    setDialogParticipantId(participantId);
    setDialogDates([]);
    setDialogError(null);

    if (!studyId) {
      setDialogError("Missing studyId");
      return;
    }

    try {
      setDialogLoading(true);
      const response = await apiService.get(
        `${API_ENDPOINTS.STUDY_ADHERENCE_MONITOR.NON_ADHERENT_DAYS}?studyId=${studyId}&participantId=${participantId}&startDate=${startDate}&endDate=${endDate}`,
      );

      if (response.success) {
        setDialogDates(response.data || []);
        // Even if empty, it's still a valid response
        if (response.data?.length === 0 && response.message) {
          setDialogError(null); // No error, just no data
        }
      } else {
        setDialogError(response.error || "Failed to load non-adherent days");
      }
    } catch (err: any) {
      setDialogError(err.message || "Error loading non-adherent days");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogParticipantId(null);
    setDialogDates([]);
    setDialogError(null);
  };

  const sortedData = useMemo(() => {
    if (sortBy === "default") {
      return [...data];
    }

    const sorted = [...data].sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (sortBy) {
        case "participantId":
          aValue = a.participantId;
          bValue = b.participantId;
          break;
        case "avgAdherence":
          aValue = a.avgAdherence;
          bValue = b.avgAdherence;
          break;
        case "totalAlerts":
          aValue = a.totalAlerts;
          bValue = b.totalAlerts;
          break;
        case "nonAdherentDays":
          aValue = a.nonAdherentDaysCount;
          bValue = b.nonAdherentDaysCount;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [data, sortBy, sortOrder]);

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              variant="h4"
              component="h3"
              sx={{ fontWeight: 600, color: "#8C1515", mb: 0 }}
            >
              Participant Metrics
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{ minWidth: "auto", p: "4px 8px", color: "text.secondary" }}
              onClick={() => setShowUsageHint(true)}
            >
              How to use?
            </Button>
          </Box>

          <Dialog
            open={showUsageHint}
            onClose={() => setShowUsageHint(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
              How to use this graph
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" paragraph>
                  Analyze individual participant behavior and identify those
                  with problematic adherence patterns. Use this data to pinpoint
                  at-risk participants who may need intervention or follow-up.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key insights:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Identify participants with low adherence rates</li>
                  <li>Find patterns in participant engagement</li>
                  <li>
                    Pinpoint at-risk participants who need follow-up or
                    intervention
                  </li>
                  <li>Monitor individual compliance trends over time</li>
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Metrics for participants with data in both TimescaleDB and
              Firestore.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              onClick={handleApplyDateRange}
              sx={{
                mt: 1,
                backgroundColor: "#8C1515",
                "&:hover": { backgroundColor: "#6B0E0E" },
              }}
            >
              Run
            </Button>
          </Stack>

          <FormControl sx={{ minWidth: 170 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
              size="small"
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="participantId">Participant ID</MenuItem>
              <MenuItem value="avgAdherence">Avg Adherence</MenuItem>
              <MenuItem value="totalAlerts">Total Alerts</MenuItem>
              <MenuItem value="nonAdherentDays">Non-Adherent Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {dateRangeError && (
          <Typography sx={{ color: "#F44336", fontSize: 12, mb: 1 }}>
            {dateRangeError}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(7)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 7 Days
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(15)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 15 Days
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleResetDateRange(30)}
              size="small"
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": { backgroundColor: "#FEF7F7" },
              }}
            >
              Last 30 Days
            </Button>
          </Stack>

          <FormControl
            sx={{
              minWidth: 120,
              visibility: sortBy === "default" ? "hidden" : "visible",
            }}
          >
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              label="Order"
              size="small"
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && hasRun && error && <Alert severity="error">{error}</Alert>}

        {!loading && !hasRun ? (
          <Alert severity="info">
            Select a date range and click Run to load this module.
          </Alert>
        ) : !loading && !error && data.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              For participants with non-adherent days &gt; 0, click on the
              number to view the specific dates where adherence was below the
              threshold configured for this study.
            </Alert>
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#FEF7F7" }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Participant ID
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Avg Adherence %
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total Alerts
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Non-Adherent Days
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((participant, index) => (
                    <TableRow
                      key={participant.participantId}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
                        "&:hover": { backgroundColor: "#FEF7F7" },
                      }}
                    >
                      <TableCell>{participant.participantId}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            backgroundColor:
                              participant.avgAdherence > 90
                                ? "#E8F5E9"
                                : participant.avgAdherence >= 80
                                  ? "#E0F2F1"
                                  : participant.avgAdherence >= 70
                                    ? "#F3E5F5"
                                    : "#FFF3E0",
                            borderRadius: 1,
                            display: "inline-block",
                            fontWeight: 600,
                          }}
                        >
                          {participant.avgAdherence}%
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {participant.totalAlerts}
                      </TableCell>
                      <TableCell align="center">
                        {participant.avgAdherence < 100 &&
                        participant.nonAdherentDaysCount > 0 ? (
                          <Button
                            size="small"
                            onClick={() =>
                              handleShowNonAdherentDays(
                                participant.participantId,
                              )
                            }
                            sx={{
                              color: "#8C1515",
                              textDecoration: "underline",
                              "&:hover": { backgroundColor: "#FEF7F7" },
                            }}
                          >
                            {participant.nonAdherentDaysCount}
                          </Button>
                        ) : (
                          <Typography variant="body2">0</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          !loading &&
          hasRun &&
          !error && (
            <Alert severity="info">
              No participants found with data in both TimescaleDB and Firestore
              for the selected date range.
            </Alert>
          )
        )}

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
            Non-Adherent Days
            {dialogParticipantId ? ` (${dialogParticipantId})` : ""}
          </DialogTitle>
          <DialogContent>
            {dialogLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            {!dialogLoading && !dialogError && dialogDates.length > 0 ? (
              <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
                {dialogDates.map((date) => (
                  <Typography key={date} variant="body2">
                    {date}
                  </Typography>
                ))}
              </Box>
            ) : (
              !dialogLoading &&
              !dialogError && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No non-adherent days in this range.
                </Typography>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ParticipantsMetricsTable;
